import React, { Component } from 'react';
import { bool, object, string, arrayOf, shape, number } from 'prop-types';
import { IntlProvider, FormattedMessage } from 'react-intl';
import LinkedIn from '@ux/icon/linkedin';
import Facebook from '@ux/icon/facebook';
import Twitter from '@ux/icon/twitter';
import Social from '@ux/icon/social';
import Link from '@ux/link';
import CurrencySelector from '@ux/currency-selector';
import golfToEid from 'golf-to-eid';
import { useBreakpoints } from '@ux/component-utilities';

const eidPrefix = 'uxp.hyd.reseller_sales_footer';

const footerLinkEid = {
  prefix: eidPrefix,
  postfix: 'link.click'
};

const utosLinkId = 'utos';

/**
 * Mapping of icon names to their Icon components.
 *
 * @type {Object}
 * @private
 */
const Icons = {
  linkedin: LinkedIn,
  facebook: Facebook,
  twitter: Twitter
};

/**
 * Helper component for social media links
 *
 * @returns {ReactElement} The social media link
 */
class SocialMediaLinkItem extends Component {
  render() {
    const { link } = this.props;
    const Icon = link && Icons[link.icon] || Social;

    return (
      <li>
        {link && link.href && link.id && link.title && (
          <Link
            href={ link?.href }
            data-eid={ golfToEid(link?.id, footerLinkEid) }
            target='_blank'
          >
            <Icon />
            <span className='sr-only'>{link?.title}</span>
          </Link>
        )}
      </li>
    );
  }
}

SocialMediaLinkItem.propTypes = {
  link: object.isRequired
};

/**
 * Render and expose the Product navigation as a Component.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} Namespaced Product Nav.
 * @api public
 */
export class ResellerSalesFooter extends Component {
  constructor(props) {
    super(props);

    this.links = props.footer;
    this.utosLink = props.legal.filter(link => link.id === utosLinkId)[0];
    this.legalLinks = props.legal.filter(link => link.id !== utosLinkId);
    this.hasSocialMediaLinks =
      props.socialMedia && props.socialMedia.length > 0;
    this.socialMediaLinks = props.socialMedia;

    this.utosLinkValues = this.utosLink && {
      ...this.utosLink,
      eid: golfToEid(this.utosLink.id, footerLinkEid)
    };
  }

  buildLink(link) {
    const linkProps = {
      ...link,
      'data-eid': golfToEid(link.id, footerLinkEid)
    };

    return <Link { ...linkProps }>{ link?.title }</Link>;
  }

  /**
   * Renders each given social media link as a SocialMediaLinkItem
   *
   * @param {Object[]} socialMediaLinks The links to render
   * @returns {SocialMediaLinkItem[]} The rendered links
   */
  renderSocialMediaLinks(socialMediaLinks) {
    return socialMediaLinks.map(socialMediaLink => (
      <SocialMediaLinkItem key={ socialMediaLink.id } link={ socialMediaLink } />
    ));
  }

  /**
   * Renders footer
   *
   * @returns {ReactElement} footer
   */
  render() {
    const { props, links, legalLinks, utosLink } = this;
    const { resellerTypeId, multiCurrencyPricingEnabled, features } = props;

    const hasLinks = !!(links && links.length);
    const hasUtos = !!utosLink;
    const hasLegalLinks = !!(legalLinks && legalLinks.length);
    const isApiReseller = resellerTypeId === 3;
    const is123Reg = props.privateLabelId === 587240;
    const doNotSellId = 'Sales:Footer:Privacy:DoNotSell';
    const doNotSellMessage = <FormattedMessage id={ doNotSellId } />;

    const termsOfService = (
      <a href={ this.utosLinkValues?.href } data-eid={ this.utosLinkValues?.eid }>
        { this.utosLinkValues?.title }
      </a>
    );
    const currencySelectorProps = {
      eidPrefix: footerLinkEid.prefix,
      market: props.market,
      currency: props.currency,
      urls: props.urls,
      privateLabelId: props.privateLabelId,
      isReseller: true,
      messages: props.messages
    };

    return (
      <IntlProvider locale={ props.market } messages={ props.messages }>
        <div className='reseller-footer' role='contentinfo'>
          <div className='container'>
            { hasLinks && (
              <ul className='reseller-footer-links'>
                { links.map((link, index) => (
                  <li key={ index }>{ this.buildLink(link) }</li>
                ))}
              </ul>
            )}
            { !isApiReseller && multiCurrencyPricingEnabled && !is123Reg && (
              <div className='currency-selector-wrapper'>
                <CurrencySelector { ...currencySelectorProps } />
              </div>
            )}
            { this.hasSocialMediaLinks && (
              <ul className='reseller-footer-social-media-links'>
                { this.renderSocialMediaLinks(this.socialMediaLinks) }
              </ul>
            )}
            { hasUtos && (
              <p className='reseller-footer-utos'>
                <FormattedMessage
                  id='SalesReseller:Footer:UToS:Simple'
                  values={{
                    termsOfService
                  }}
                />
              </p>
            )}
            {hasLegalLinks && (
              <ul className='reseller-footer-legal'>
                { legalLinks.map((link, index) => (
                  <li key={ index }>{ this.buildLink(link) }</li>
                )) }
                { features.doNotSellLink && props.urls.cookies.href && (
                  <li className='reseller-footer-legal'>
                    <Link
                      className='reseller-footer-do-not-sell'
                      urlArgs={ props.urlArgs }
                      href={ props.urls.cookies.href }
                      data-eid={ golfToEid(doNotSellId, footerLinkEid) }>
                      { doNotSellMessage }
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      </IntlProvider>
    );
  }
}

const linksPropType = arrayOf(
  shape({
    href: string.isRequired,
    title: string.isRequired,
    id: string.isRequired
  })
).isRequired;

ResellerSalesFooter.propTypes = {
  breakpoint: string,
  messages: object.isRequired,
  market: string.isRequired,
  resellerTypeId: number.isRequired,
  features: object,
  footer: linksPropType,
  legal: linksPropType,
  socialMedia: linksPropType,
  multiCurrencyPricingEnabled: bool,
  currency: string,
  urls: object,
  urlArgs: object,
  privateLabelId: number
};

export default function ResellerSalesFooterWrap(props) {
  const { breakpoint } = useBreakpoints('mobile', 'phablet', 'tablet', 'desktop', 'large');
  return <ResellerSalesFooter { ...props } breakpoint={ breakpoint }/>;
}

ResellerSalesFooterWrap.propTypes = {
};
